import { z } from "zod/v4";

import { calendarDayInZone, zonedWallClockToUtc } from "./pass-status";

/** 23:59 — the value every organization starts with, reproducing the
 *  behaviour from before the setting existed. */
export const DEFAULT_DAY_END_MINUTES = 1439;

/**
 * `organization.day_end_minutes`: minutes since LOCAL midnight in the
 * organization's timezone (0–1439), the same convention as the recurring
 * pass's `dailyEndMinutes`. A wall-clock rule, so it survives DST/offset
 * changes where a stored instant would freeze the offset.
 */
export const dayEndMinutesSchema = z.number().int().min(0).max(1439);

/** The instant `dayEndMinutes` falls on, on the given calendar day of `timeZone`. */
export function dayEndUtc(
  day: { year: number; month: number; day: number },
  dayEndMinutes: number,
  timeZone: string,
): Date {
  return zonedWallClockToUtc(
    day.year,
    day.month,
    day.day,
    Math.floor(dayEndMinutes / 60),
    dayEndMinutes % 60,
    timeZone,
  );
}

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * The single point of truth for a one_time invitation authored without an
 * end (spec 2026-08-27-invite-import-design §3.3):
 *
 * | validFrom | validTo | result                                              |
 * |-----------|---------|-----------------------------------------------------|
 * | absent    | absent  | today 00:00 → today day-end (org clock)             |
 * |           |         | (now past day-end → 23:59, then now + 1 h)          |
 * | present   | absent  | day-end on validFrom's calendar day                 |
 * | present   | present | unchanged                                           |
 * | absent    | present | unchanged (legacy)                                  |
 *
 * Guard: a start at or after that day's day-end (evening event, 18:00
 * closing) gets 23:59 that day instead; a start at 23:59 itself gets one
 * hour. The window is never empty or inverted.
 */
export function resolveOneTimeWindow(args: {
  validFrom: Date | null;
  validTo: Date | null;
  now: Date;
  timeZone: string;
  dayEndMinutes: number;
}): { validFrom: Date | null; validTo: Date | null } {
  const { timeZone, dayEndMinutes } = args;
  if (args.validTo) return { validFrom: args.validFrom, validTo: args.validTo };

  if (!args.validFrom) {
    const today = calendarDayInZone(args.now, timeZone);
    // Same guard as a start at or after day-end, against NOW: a pass authored
    // at 19:00 in an org whose day ends at 18:00 would otherwise be born
    // expired (and a self-fill guest's link with it).
    let validTo = dayEndUtc(today, dayEndMinutes, timeZone);
    if (validTo <= args.now) {
      validTo = dayEndUtc(today, DEFAULT_DAY_END_MINUTES, timeZone);
    }
    if (validTo <= args.now) {
      validTo = new Date(args.now.getTime() + ONE_HOUR_MS);
    }
    return {
      validFrom: zonedWallClockToUtc(
        today.year,
        today.month,
        today.day,
        0,
        0,
        timeZone,
      ),
      validTo,
    };
  }

  const day = calendarDayInZone(args.validFrom, timeZone);
  let validTo = dayEndUtc(day, dayEndMinutes, timeZone);
  if (validTo <= args.validFrom) {
    validTo = dayEndUtc(day, DEFAULT_DAY_END_MINUTES, timeZone);
  }
  if (validTo <= args.validFrom) {
    validTo = new Date(args.validFrom.getTime() + ONE_HOUR_MS);
  }
  return { validFrom: args.validFrom, validTo };
}

/** `organization.setDayEnd` input. */
export const setDayEndInput = z.object({ dayEndMinutes: dayEndMinutesSchema });
export type SetDayEndInput = z.infer<typeof setDayEndInput>;

/** The most recent org day-end at or before `now`, as an instant — the start
 *  of the présence window.
 *
 *  The mirror of the auto-close janitor's rule ("the first day-end strictly
 *  after `created_at`"): a row the janitor has not yet had a reason to close
 *  is a row this window still shows. Keying off plain local midnight instead
 *  breaks that symmetry — an org whose day ends at 18:00 would drop a 19:00
 *  arrival off « Sur site » at 00:00, eighteen hours before it is due to
 *  close, still open in the database and missing from the evacuation count.
 *
 *  The présence list and « Sur site (N) » are evacuation figures: the wrong
 *  boundary either drops people who entered last night or resurrects
 *  yesterday's visitors, and both failures are silent. `Date#setHours` gives
 *  the SERVER's midnight, which is only ever right by accident. */
export function presenceWindowStart(
  now: Date,
  timeZone: string,
  dayEndMinutes: number,
): Date {
  const today = calendarDayInZone(now, timeZone);
  const todayEnd = dayEndUtc(today, dayEndMinutes, timeZone);
  if (todayEnd.getTime() <= now.getTime()) return todayEnd;
  // Step a calendar day back on the PARTS, never by subtracting 24h from an
  // instant: across a DST shift a day is 23 or 25 hours long and the
  // subtraction lands on the wrong date. `month` is 0-based on both sides.
  const prev = new Date(Date.UTC(today.year, today.month, today.day - 1));
  return dayEndUtc(
    {
      year: prev.getUTCFullYear(),
      month: prev.getUTCMonth(),
      day: prev.getUTCDate(),
    },
    dayEndMinutes,
    timeZone,
  );
}
